import { useEffect, useState } from "react";
import { Lightbulb, Loader2, ListOrdered, Settings, Wrench, X } from "lucide-react";
import { BuildGuidePopup } from "./BuildGuidePopup";
import { BuildSetupPopup } from "./BuildSetupPopup";
import { barColor } from "../reviews";
import { Avatar } from "./Avatar";
import { useInitiativeAssist } from "../hooks/useInitiativeAssist";
import type { BuildPathResult, BuildPlanResult } from "../hooks/useInitiativeAssist";
import type { InitiativeItem } from "../types/navigator";
import { logActivity } from "../api/activityLog";

interface Props {
  items: InitiativeItem[];
  onClose: () => void;
  onSelect: (item: InitiativeItem) => void;
}

/** Average of Business Value + Technical Feasibility — mirrors the same
 *  calculation AdminTable's "Avg Score" column uses, so the ranking here
 *  matches what's shown in the table. `null` when neither score exists. */
function avgScore(item: InitiativeItem): number | null {
  const biz = Number(item.businessValueScore) || 0;
  const tech = Number(item.technicalFeasibilityScore) || 0;
  if (!item.businessValueScore && !item.technicalFeasibilityScore) return null;
  return (biz + tech) / 2;
}

/** Every field captured on the initiative, flattened for the AI prompt —
 *  same shape as ReviewModal's initiativeToFields, kept local here since
 *  this works straight off InitiativeItem rather than the review modal's
 *  looser InitiativePrefill. */
function itemToFields(item: InitiativeItem): { label: string; value: string }[] {
  const pairs: [string, string | undefined][] = [
    ["Title", item.title],
    ["Team / function", item.team],
    ["Region / division", item.region],
    ["Problem", item.problem],
    ["How often it happens", item.problemFrequency],
    ["Who's impacted", item.impactedToday],
    ["What success looks like", item.ifSolved],
    ["Anticipated impact", item.anticipatedImpact],
    ["Type of impact", item.impactType],
    ["Proposed solution", item.proposedSolution],
    ["Existing examples", item.existingExamples],
    ["Data/inputs involved", item.dataInputs],
    ["Constraints/risks", item.constraintsRisks],
    ["Anything else", item.anythingElse],
  ];
  return pairs
    .filter(([, value]) => value != null && value !== "")
    .map(([label, value]) => ({ label, value: value! }));
}

const PATH_LABELS: Record<BuildPathResult["path"], string> = {
  citizen: "AI Builder",
  hybrid: "Hybrid",
  engineer: "Engineer needed",
};

function BuildPathBadge({ result }: { result: BuildPathResult }) {
  return (
    <div className={`backlog-buildpath backlog-buildpath--${result.path}`}>
      <div className="backlog-buildpath-head">
        <span className="backlog-buildpath-label">{PATH_LABELS[result.path]}</span>
        {result.stack.map((s) => (
          <span key={s} className="backlog-stack-chip">
            {s}
          </span>
        ))}
      </div>
      {result.reasoning ? (
        <p className="backlog-buildpath-reasoning">{result.reasoning}</p>
      ) : null}
    </div>
  );
}

export function BacklogPriorityModal({ items, onClose, onSelect }: Props) {
  const { buildPlan } = useInitiativeAssist();
  // One AI call per item produces the whole plan — build path, stack,
  // approach and hands-on setup — so the badge and both popups always agree.
  // They are three views of the same cached result, not three separate calls.
  const [plans, setPlans] = useState<Record<string, BuildPlanResult>>({});
  const [planning, setPlanning] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [guideFor, setGuideFor] = useState<InitiativeItem | null>(null);
  const [setupFor, setSetupFor] = useState<InitiativeItem | null>(null);

  const runPlan = async (item: InitiativeItem) => {
    if (plans[item.id] || planning[item.id]) return;
    setErrors((e) => ({ ...e, [item.id]: "" }));
    setPlanning((p) => ({ ...p, [item.id]: true }));
    try {
      const plan = await buildPlan(itemToFields(item));
      setPlans((p) => ({ ...p, [item.id]: plan }));
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [item.id]: err instanceof Error ? err.message : String(err),
      }));
    } finally {
      setPlanning((p) => ({ ...p, [item.id]: false }));
    }
  };

  const openGuide = (item: InitiativeItem) => {
    setGuideFor(item);
    void runPlan(item);
  };

  const openSetup = (item: InitiativeItem) => {
    setSetupFor(item);
    void runPlan(item);
  };

  useEffect(() => {
    void logActivity("Feature", "NextUpOpened", "Opened the Next up backlog priority list");
  }, []);

  const ranked = items
    .filter((i) => (i.status || "").trim().toLowerCase() === "in backlog")
    .map((i) => ({ item: i, score: avgScore(i) }))
    .sort((a, b) => (b.score ?? -1) - (a.score ?? -1));

  return (
    <>
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal backlog-modal" role="dialog" aria-modal="true">
        <div className="modal-accent" aria-hidden />
        <div className="modal-head">
          <div className="modal-head-icon icon-initiative" aria-hidden>
            <ListOrdered size={22} />
          </div>
          <div className="modal-head-text">
            <span className="modal-kind">Backlog priority</span>
            <h2 className="modal-title">Next up</h2>
            <p className="add-item-subtitle">
              Backlog initiatives ranked by average score — highest first.
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="backlog-list">
          {ranked.length === 0 ? (
            <div className="backlog-empty">Nothing in the backlog right now.</div>
          ) : (
            ranked.map(({ item, score }, i) => (
              <div key={item.id} className="backlog-row">
                <div className="backlog-row-top">
                  <span className="backlog-rank">{i + 1}</span>
                  <button
                    type="button"
                    className="backlog-row-main"
                    onClick={() => onSelect(item)}
                  >
                    <span className="backlog-row-title">
                      {item.title || "Untitled initiative"}
                    </span>
                    <span className="backlog-row-meta">
                      <Avatar name={item.submittedBy || "?"} size={18} />
                      <span>{item.submittedBy || "Unknown"}</span>
                      {item.team ? <span>· {item.team}</span> : null}
                      {item.region ? <span>· {item.region}</span> : null}
                    </span>
                  </button>
                  <span className="backlog-row-scores">
                    <span className="backlog-subscore" title="Business value">
                      B {item.businessValueScore ? Number(item.businessValueScore).toFixed(2) : "—"}
                    </span>
                    <span className="backlog-subscore" title="Technical feasibility">
                      T {item.technicalFeasibilityScore ? Number(item.technicalFeasibilityScore).toFixed(2) : "—"}
                    </span>
                    <span
                      className="backlog-avg"
                      style={score != null ? { background: barColor(score) } : undefined}
                    >
                      {score != null ? score.toFixed(2) : "—"}
                    </span>
                  </span>
                </div>

                <div className="backlog-row-assess">
                  {plans[item.id] ? (
                    <BuildPathBadge result={plans[item.id]} />
                  ) : (
                    <button
                      type="button"
                      className="backlog-assess-btn"
                      disabled={!!planning[item.id]}
                      onClick={() => runPlan(item)}
                    >
                      {planning[item.id] ? (
                        <>
                          <Loader2 size={12} className="add-item-spin" /> Building plan…
                        </>
                      ) : (
                        <>
                          <Wrench size={12} /> Assess build path
                        </>
                      )}
                    </button>
                  )}
                  {errors[item.id] ? (
                    <span className="backlog-assess-error">{errors[item.id]}</span>
                  ) : null}
                  <button
                    type="button"
                    className="backlog-assess-btn"
                    title="The approach for building this — part of the same plan as the build path"
                    onClick={() => openGuide(item)}
                  >
                    <Lightbulb size={12} /> Proposed solution
                  </button>
                  <button
                    type="button"
                    className="backlog-assess-btn"
                    title="Hands-on setup steps and ready-to-paste snippets — part of the same plan as the build path"
                    onClick={() => openSetup(item)}
                  >
                    <Settings size={12} /> How to configure
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>

    {guideFor ? (
      <BuildGuidePopup
        title={guideFor.title}
        path={plans[guideFor.id]?.path}
        loading={!!planning[guideFor.id]}
        error={errors[guideFor.id] || null}
        guide={plans[guideFor.id]?.guide ?? null}
        onRetry={() => void runPlan(guideFor)}
        onClose={() => setGuideFor(null)}
      />
    ) : null}

    {setupFor ? (
      <BuildSetupPopup
        title={setupFor.title}
        path={plans[setupFor.id]?.path}
        loading={!!planning[setupFor.id]}
        error={errors[setupFor.id] || null}
        setup={plans[setupFor.id]?.setup ?? null}
        onRetry={() => void runPlan(setupFor)}
        onClose={() => setSetupFor(null)}
      />
    ) : null}
    </>
  );
}
